import { useState } from "react";
import {
  UserCheck,
  ListChecks,
  Rocket,
  TrendingUp,
  Plus,
  Minus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const features = [
  {
    id: "one-to-one",
    icon: UserCheck,
    title: "One-to-One Attention",
    short:
      "Small batch sizes that let every student be seen, heard, and guided personally.",
    bullets: [
      "Individual doubt-solving with the subject teacher",
      "Personal mentor tracks each student's weekly progress",
      "Customised practice based on weak areas",
      "Direct parent-teacher communication",
    ],
  },
  {
    id: "structured",
    icon: ListChecks,
    title: "Structured Preparation",
    short:
      "A tightly designed academic system built over 26 years of teaching experience.",
    bullets: [
      "Board-pattern aligned teaching",
      "Daily classroom practice problems",
      "Topic-wise printed notes & worksheets",
      "Weekly, monthly and yearly tests",
      "Planned revision strategy before exams",
      "Doubt-solving sessions twice a week",
      "Time-management drills for board exams",
      "Concept-mapping and summary sheets",
      "Performance reports shared with parents",
      "Pre-board mock test series",
    ],
  },
  {
    id: "competitive",
    icon: Rocket,
    title: "Competitive Corner",
    short:
      "Integrated coaching for students preparing for entrance examinations alongside school boards.",
    bullets: [
      "JEE — Mathematics, Physics & Chemistry foundations and advanced problem solving",
      "NEET — Biology focus along with Physics & Chemistry NCERT mastery",
      "MHT-CET — State-level pattern, MCQ speed practice and full-length mocks",
      "Foundation programme starting Std 9 / 10",
      "Separate doubt-clearing slots for entrance prep",
    ],
  },
  {
    id: "proven",
    icon: TrendingUp,
    title: "Proven Results",
    short:
      "A 100% result-oriented system trusted by 15,000+ students and their families.",
    bullets: [
      "26+ years of consistent academic outcomes",
      "Strong scoring track record across SSC & HSC",
      "Year-on-year toppers from the institute",
      "Honest, data-driven student counselling",
    ],
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-slate-50 py-16 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="Why Gurukul"
          title="Built for clarity, calm, and consistent results."
          subtitle="Every part of how we teach is designed around one simple idea — the student should never feel lost."
        />

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
          {features.map((f) => (
            <FeatureCard key={f.id} feature={f} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature }) {
  const [open, setOpen] = useState(false);
  const Icon = feature.icon;

  return (
    <div
      data-testid={`feature-card-${feature.id}`}
      className="group rounded-md border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-red-200 hover:shadow-md md:p-8"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600 transition-colors group-hover:bg-red-600 group-hover:text-white">
          <Icon className="h-6 w-6" strokeWidth={2.1} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-bold text-slate-900 md:text-xl">
            {feature.title}
          </h3>
          <p className="mt-1.5 text-[14px] leading-relaxed text-slate-600 md:text-[15px]">
            {feature.short}
          </p>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={`feature-toggle-${feature.id}`}
            aria-expanded={open}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            {open ? (
              <>
                <Minus className="h-3.5 w-3.5" /> Hide details
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" /> View details
              </>
            )}
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.ul
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                className="mt-4 space-y-2 overflow-hidden text-[14px] text-slate-700"
              >
                {feature.bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 leading-relaxed"
                  >
                    <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-600" />
                    <span>{b}</span>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export function SectionTitle({ eyebrow, title, subtitle, align = "left" }) {
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : ""}>
      {eyebrow && (
        <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-red-700">
          {eyebrow}
        </div>
      )}
      <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-600 md:text-base">
          {subtitle}
        </p>
      )}
    </div>
  );
}
